import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  Users, Wallet, FileText, CalendarBlank, Phone, Package,
  TrendUp, ArrowRight, Clock, CheckCircle, Receipt, ChartLine, Bell,
} from '@phosphor-icons/react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import { Amount, useAmountVisibility } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => '₹' + (Number(n) || 0).toLocaleString('en-IN');
const today = () => new Date().toISOString().split('T')[0];

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const LEAD_STATUS_ORDER = ['New', 'Contacted', 'Qualified', 'Proposal Sent', 'Won', 'Lost'];

// Tile that respects the global "hide amounts" toggle when `isAmount` is true.
const StatTile = ({ icon: Icon, label, value, sub, color, to, isAmount = false }) => {
  const inner = (
    <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full" data-testid={`tile-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className="rounded-lg p-2" style={{ background: `${color}15` }}>
          <Icon size={20} weight="duotone" style={{ color }} />
        </div>
        {to && <ArrowRight size={14} style={{ color: '#94a3b8' }} />}
      </div>
      <div className="mt-2.5">
        <div className="text-2xl font-black tracking-tight" style={{ color: 'var(--dx-text)' }}>
          {isAmount ? <Amount value={value} /> : value}
        </div>
        <div className="text-[10px] uppercase font-bold tracking-wide mt-0.5" style={{ color: '#64748b' }}>{label}</div>
        {sub && <div className="text-[11px] mt-0.5" style={{ color: '#94a3b8' }}>{sub}</div>}
      </div>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const DashboardPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { hidden: amountsHidden } = useAmountVisibility();
  const [data, setData] = useState({
    employees: [], leads: [], invoices: [], appointments: [], assets: { categories: [], total: 0 },
    checkinDay: null, leaveBalances: [], quotes: [], proposals: [], reminders: [],
  });
  const [analytics, setAnalytics] = useState(null);
  const [billingAlerts, setBillingAlerts] = useState({ near_budget: [], under_invoiced: [], over_budget: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const fetchOrEmpty = (url, params) => axios.get(`${API_URL}${url}`, { params, withCredentials: true }).catch(() => ({ data: [] }));
    const fetchOrNull  = (url, params) => axios.get(`${API_URL}${url}`, { params, withCredentials: true }).catch(() => ({ data: null }));
    try {
      const [emp, leads, invoices, appts, assetSummary, checkinDay, leaves, quotes, proposals, reminders] = await Promise.all([
        fetchOrEmpty('/api/employees'),
        fetchOrEmpty('/api/leads'),
        fetchOrEmpty('/api/invoices'),
        fetchOrEmpty('/api/appointments', { from_date: today() }),
        fetchOrEmpty('/api/assets/summary'),
        fetchOrNull('/api/checkin/admin/day', { date: today() }),
        fetchOrEmpty('/api/leaves/balances'),
        fetchOrEmpty('/api/quotes'),
        fetchOrEmpty('/api/proposals'),
        fetchOrEmpty('/api/reminders', { status: 'open' }),
      ]);
      setData({
        employees: emp.data || [],
        leads: leads.data || [],
        invoices: invoices.data || [],
        appointments: appts.data || [],
        assets: assetSummary.data || { categories: [], total: 0 },
        checkinDay: checkinDay.data,
        leaveBalances: leaves.data || [],
        quotes: quotes.data || [],
        proposals: proposals.data || [],
        reminders: reminders.data || [],
      });
      // Load admin-only analytics (best-effort)
      try {
        const r = await axios.get(`${API_URL}/api/dashboard/analytics`, { withCredentials: true });
        setAnalytics(r.data || null);
      } catch { /* non-admin or first run */ }
      // Load project billing alerts (best-effort — needs projects module)
      try {
        const b = await axios.get(`${API_URL}/api/projects-billing/alerts`, { withCredentials: true });
        setBillingAlerts(b.data || { near_budget: [], under_invoiced: [], over_budget: [] });
      } catch { /* no access — skip */ }
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // === Derived stats ===
  const totalRev = useMemo(() =>
    data.invoices.filter((i) => i.status === 'paid').reduce((a, b) => a + (Number(b.total) || 0), 0),
  [data.invoices]);
  const outstanding = useMemo(() =>
    data.invoices.filter((i) => i.status !== 'paid' && i.status !== 'draft').reduce((a, b) => a + (Number(b.total) || 0), 0),
  [data.invoices]);

  const activeEmployees = data.employees.filter((e) => e.is_active !== false).length;
  const todayAppts = data.appointments.filter((a) => a.date === today());
  const upcomingAppts = data.appointments.filter((a) => a.date > today()).slice(0, 5);

  // Revenue last 6 months from paid invoices
  const revByMonth = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({ key, label: d.toLocaleDateString(undefined, { month: 'short' }), revenue: 0, count: 0 });
    }
    const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
    data.invoices.forEach((inv) => {
      if (inv.status !== 'paid') return;
      const d = inv.invoice_date || inv.created_at;
      if (!d) return;
      const k = d.slice(0, 7);
      if (idx[k] !== undefined) {
        months[idx[k]].revenue += Number(inv.total) || 0;
        months[idx[k]].count += 1;
      }
    });
    return months;
  }, [data.invoices]);

  // Leads by status
  const leadsByStatus = useMemo(() => {
    const map = {};
    data.leads.forEach((l) => { map[l.status] = (map[l.status] || 0) + 1; });
    return LEAD_STATUS_ORDER.map((s) => ({ name: s, value: map[s] || 0 }));
  }, [data.leads]);

  // Assets pie
  const assetsPie = useMemo(() => (data.assets.categories || []).filter((c) => c.count > 0).slice(0, 8), [data.assets]);

  // Today check-in summary
  const ci = data.checkinDay;
  const ciCounts = useMemo(() => {
    if (!ci) return { present: 0, working: 0, absent: 0, no_punch: 0 };
    const c = { present: 0, working: 0, absent: 0, no_punch: 0 };
    (ci.rows || []).forEach((r) => {
      if (r.status === 'present') c.present++;
      else if (r.status === 'in_progress') c.working++;
      else if (r.status === 'absent' || r.status === 'incomplete') c.absent++;
      else c.no_punch++;
    });
    return c;
  }, [ci]);

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Hero */}
      <div className="rounded-xl p-5 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#1d4ed8 100%)' }}>
        <div className="relative z-10">
          <div className="text-xs uppercase tracking-widest opacity-70">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">Welcome back, {user?.name?.split(' ')[0] || 'Admin'}</h1>
          <p className="text-sm opacity-80 mt-1">Here's what's happening at Dextra today</p>
          {todayAppts.length > 0 && (
            <p className="text-xs mt-3 px-3 py-1.5 rounded-full inline-block" style={{ background: 'rgba(255,255,255,.15)' }}>
              📅 You have <strong>{todayAppts.length}</strong> appointment{todayAppts.length !== 1 ? 's' : ''} today
            </p>
          )}
        </div>
        <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(white,transparent 70%)' }} />
      </div>

      {/* Stat tiles row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={Users} label="Active Staff" value={activeEmployees} sub={`${data.employees.length - activeEmployees} ex-employees`} color="#3b82f6" to="/employees" />
        <StatTile icon={Phone} label="Open Leads" value={data.leads.filter((l) => ['New', 'Contacted', 'Qualified'].includes(l.status)).length} sub={`${data.leads.length} total leads`} color="#10b981" to="/leads" />
        <StatTile icon={Wallet} label="Revenue (Paid)" value={totalRev} isAmount sub={`${data.invoices.filter((i) => i.status === 'paid').length} invoices`} color="#f59e0b" to="/invoices" />
        <StatTile icon={Receipt} label="Outstanding" value={outstanding} isAmount sub={`${data.invoices.filter((i) => i.status !== 'paid' && i.status !== 'draft').length} unpaid`} color="#ef4444" to="/invoices" />
      </div>

      {/* Project Billing Alerts — fixed-scope projects nearing or exceeding budget, or forgotten */}
      {(billingAlerts.near_budget.length > 0 || billingAlerts.under_invoiced.length > 0 || billingAlerts.over_budget.length > 0) && (
        <Card className="p-4" data-testid="billing-alerts-card">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5">
            💰 Project Billing Alerts
            <span className="text-[10px] font-normal text-gray-500">Fixed-scope projects — react before it slips your mind</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: 'over_budget', title: '⚠ Over Budget', items: billingAlerts.over_budget, tint: 'bg-rose-50 border-rose-200', text: 'text-rose-800', pctColor: 'text-rose-700' },
              { key: 'near_budget', title: '🔥 Near Budget (≥80%)', items: billingAlerts.near_budget, tint: 'bg-amber-50 border-amber-200', text: 'text-amber-800', pctColor: 'text-amber-700' },
              { key: 'under_invoiced', title: '😴 Under-invoiced (>30d, <20%)', items: billingAlerts.under_invoiced, tint: 'bg-blue-50 border-blue-200', text: 'text-blue-800', pctColor: 'text-blue-700' },
            ].map((bucket) => (
              <div key={bucket.key} className={`rounded-lg p-3 border ${bucket.tint}`} data-testid={`billing-bucket-${bucket.key}`}>
                <div className={`text-xs font-bold uppercase tracking-wide mb-2 ${bucket.text}`}>
                  {bucket.title} · {bucket.items.length}
                </div>
                {bucket.items.length === 0 ? (
                  <div className="text-[11px] text-gray-500 italic">None — you&apos;re good ✓</div>
                ) : (
                  <ul className="space-y-1.5">
                    {bucket.items.slice(0, 5).map((b) => (
                      <li key={b.project_id} className="text-[11px]">
                        <a href="/projects" className="hover:underline">
                          <span className="font-semibold">{b.project_name}</span>
                          <span className={`ml-1 ${bucket.pctColor} font-bold`}>({b.pct}%)</span>
                        </a>
                        <div className="text-[10px] text-gray-600">
                          {b.contract_currency} {new Intl.NumberFormat('en-IN').format(Math.round(b.contract_value))} contract
                        </div>
                        <div className="text-[10px]">
                          <span className="text-blue-700">₹ {new Intl.NumberFormat('en-IN').format(Math.round(b.invoiced_inr))} inv</span> ·
                          <span className="text-emerald-700"> ✓ {new Intl.NumberFormat('en-IN').format(Math.round(b.paid_inr))}</span> ·
                          <span className="text-rose-700"> {new Intl.NumberFormat('en-IN').format(Math.round(Math.max(0, b.invoiced_inr - b.paid_inr)))} due</span>
                        </div>
                      </li>
                    ))}
                    {bucket.items.length > 5 && <li className="text-[10px] text-gray-500 italic">+{bucket.items.length - 5} more…</li>}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Stat tiles row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={CalendarBlank} label="Today's Appts" value={todayAppts.length} sub={`${data.appointments.length} upcoming`} color="#8b5cf6" to="/appointments" />
        <StatTile icon={FileText} label="Active Proposals" value={data.proposals.filter((p) => !['Won', 'Lost'].includes(p.status)).length} sub={`${data.proposals.length} total`} color="#06b6d4" to="/proposals" />
        <StatTile icon={Package} label="Assets" value={data.assets.total} sub={`${data.assets.categories?.filter((c) => c.in_use > 0).reduce((a, b) => a + b.in_use, 0) || 0} in use`} color="#ec4899" to="/assets" />
        <StatTile icon={Clock} label="Today Check-ins" value={ciCounts.present + ciCounts.working} sub={`${ciCounts.absent} absent · ${ciCounts.no_punch} pending`} color="#84cc16" to="/checkin-dashboard" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue trend */}
        <Card className="p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-bold flex items-center gap-1.5"><TrendUp size={16} /> Revenue Trend</h3>
              <p className="text-[10px]" style={{ color: '#94a3b8' }}>Last 6 months · paid invoices only</p>
            </div>
            <div className="text-right">
              <div className="text-xl font-black"><Amount value={revByMonth.reduce((a, b) => a + b.revenue, 0)} /></div>
              <div className="text-[10px] uppercase font-bold" style={{ color: '#10b981' }}>Total 6M</div>
            </div>
          </div>
          {amountsHidden ? (
            <div className="h-[220px] flex flex-col items-center justify-center bg-slate-50 rounded-lg border border-dashed border-slate-200 text-slate-400">
              <div className="text-3xl">🔒</div>
              <div className="text-xs mt-1.5 font-medium">Revenue chart hidden</div>
              <div className="text-[10px] mt-0.5">Click the eye icon in the header to show</div>
            </div>
          ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={revByMonth} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="revG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmtINR(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2.5} fill="url(#revG)" />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </Card>

        {/* Leads funnel */}
        <Card className="p-4">
          <h3 className="text-sm font-bold mb-1 flex items-center gap-1.5"><Phone size={16} /> Leads Funnel</h3>
          <p className="text-[10px] mb-3" style={{ color: '#94a3b8' }}>{data.leads.length} total</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={leadsByStatus} layout="vertical" margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {leadsByStatus.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Bottom row: Appointments + Assets pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Today's & upcoming appointments */}
        <Card className="p-0 lg:col-span-2 overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center gap-1.5"><CalendarBlank size={16} /> Today & Upcoming Appointments</h3>
            <Link to="/appointments"><Button size="sm" variant="outline">View All →</Button></Link>
          </div>
          <div className="divide-y">
            {todayAppts.length === 0 && upcomingAppts.length === 0 ? (
              <div className="p-8 text-center text-sm" style={{ color: '#94a3b8' }}>No appointments scheduled.</div>
            ) : (
              [...todayAppts, ...upcomingAppts].slice(0, 6).map((a) => (
                <div key={a.id} className="p-3 hover:bg-gray-50 flex items-start gap-3" data-testid={`dash-appt-${a.id}`}>
                  <div className="flex-shrink-0 rounded-lg p-2 text-center" style={{ background: a.date === today() ? '#dbeafe' : '#f1f5f9', minWidth: 60 }}>
                    <div className="text-[9px] uppercase font-bold" style={{ color: a.date === today() ? '#1e40af' : '#64748b' }}>
                      {a.date === today() ? 'TODAY' : new Date(a.date).toLocaleDateString(undefined, { month: 'short' }).toUpperCase()}
                    </div>
                    <div className="text-base font-black" style={{ color: a.date === today() ? '#1e40af' : '#334155' }}>
                      {a.date === today() ? a.start_time : new Date(a.date).getDate()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{a.title}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: '#64748b' }}>
                      {a.client_name && <>👤 {a.client_name} · </>}
                      {a.team_member_names?.length > 0 && <>👥 {a.team_member_names.slice(0, 2).join(', ')}{a.team_member_names.length > 2 ? ` +${a.team_member_names.length - 2}` : ''}</>}
                    </div>
                    {a.location && <div className="text-[10px] mt-0.5" style={{ color: '#94a3b8' }}>📍 {a.location}</div>}
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                    background: a.status === 'Done' ? '#dcfce7' : a.status === 'Confirmed' ? '#dbeafe' : '#fef3c7',
                    color: a.status === 'Done' ? '#15803d' : a.status === 'Confirmed' ? '#1e40af' : '#92400e',
                  }}>{a.status.toUpperCase()}</span>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Assets distribution */}
        <Card className="p-4">
          <h3 className="text-sm font-bold mb-1 flex items-center gap-1.5"><Package size={16} /> Asset Distribution</h3>
          <p className="text-[10px] mb-3" style={{ color: '#94a3b8' }}>{data.assets.total} total assets</p>
          {data.assets.total === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-center">
              <div>
                <Package size={32} style={{ color: '#cbd5e1', margin: '0 auto 8px' }} />
                <p className="text-xs" style={{ color: '#94a3b8' }}>No assets yet</p>
                <Link to="/assets" className="text-xs text-blue-600 hover:underline">Add first asset →</Link>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={assetsPie} dataKey="count" nameKey="category" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {assetsPie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend verticalAlign="bottom" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Analytics row: Payroll trend + Tasks by status donut + Permissions */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="analytics-row">
          <Card className="p-4">
            <h3 className="text-sm font-bold mb-1 flex items-center gap-1.5"><Wallet size={16} /> Payroll Trend (6 months)</h3>
            <p className="text-[10px] mb-2" style={{ color: '#94a3b8' }}>Net payroll disbursed per month</p>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={analytics.payroll_trend || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="payrollGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmtINR(v)} />
                <Area type="monotone" dataKey="net" stroke="#10b981" strokeWidth={2} fill="url(#payrollGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-4">
            <h3 className="text-sm font-bold mb-1 flex items-center gap-1.5"><ChartLine size={16} /> Tasks by Status</h3>
            <p className="text-[10px] mb-2" style={{ color: '#94a3b8' }}>{(analytics.tasks_by_status || []).reduce((a, b) => a + b.count, 0)} tasks total</p>
            {(!analytics.tasks_by_status || analytics.tasks_by_status.length === 0) ? (
              <div className="h-[200px] flex items-center justify-center text-xs" style={{ color: '#94a3b8' }}>No tasks yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={analytics.tasks_by_status}
                    dataKey="count" nameKey="status"
                    cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3}
                  >
                    {(analytics.tasks_by_status || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend verticalAlign="bottom" iconSize={8} wrapperStyle={{ fontSize: 10 }} formatter={(v) => v.replace(/_/g, ' ')} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="p-4">
            <h3 className="text-sm font-bold mb-1 flex items-center gap-1.5"><Clock size={16} /> Permissions · {analytics.permissions?.month}</h3>
            <p className="text-[10px] mb-2" style={{ color: '#94a3b8' }}>{analytics.permissions?.total || 0} requests this month</p>
            {(!analytics.permissions?.by_type || analytics.permissions.by_type.length === 0) ? (
              <div className="h-[200px] flex items-center justify-center text-xs" style={{ color: '#94a3b8' }}>No permission requests yet this month</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.permissions.by_type || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="type" tick={{ fontSize: 9 }} tickFormatter={(v) => v.replace('_', ' ').slice(0, 8)} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {(analytics.permissions.by_type || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            {(analytics.permissions?.by_status || []).length > 0 && (
              <div className="flex items-center justify-around mt-2 pt-2 border-t text-[10px]">
                {analytics.permissions.by_status.map((s) => (
                  <div key={s.status} className="text-center">
                    <div className="font-bold" style={{ color: s.status === 'approved' ? '#15803d' : s.status === 'rejected' ? '#b91c1c' : '#92400e' }}>{s.count}</div>
                    <div className="uppercase tracking-wider" style={{ color: '#94a3b8' }}>{s.status}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Bottom row: check-in summary + quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-1.5"><Clock size={16} /> Today's Attendance</h3>
            <Link to="/checkin-dashboard"><Button size="sm" variant="outline">Details →</Button></Link>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="rounded-lg p-3 text-center" style={{ background: '#dcfce7' }}>
              <div className="text-2xl font-black" style={{ color: '#15803d' }}>{ciCounts.present}</div>
              <div className="text-[9px] uppercase font-bold mt-0.5" style={{ color: '#15803d' }}>Present</div>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: '#fef3c7' }}>
              <div className="text-2xl font-black" style={{ color: '#92400e' }}>{ciCounts.working}</div>
              <div className="text-[9px] uppercase font-bold mt-0.5" style={{ color: '#92400e' }}>Working Now</div>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: '#fee2e2' }}>
              <div className="text-2xl font-black" style={{ color: '#b91c1c' }}>{ciCounts.absent}</div>
              <div className="text-[9px] uppercase font-bold mt-0.5" style={{ color: '#b91c1c' }}>Absent</div>
            </div>
            <div className="rounded-lg p-3 text-center" style={{ background: '#f1f5f9' }}>
              <div className="text-2xl font-black" style={{ color: '#64748b' }}>{ciCounts.no_punch}</div>
              <div className="text-[9px] uppercase font-bold mt-0.5" style={{ color: '#64748b' }}>No Punch</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-1.5"><ChartLine size={16} /> Quick Actions</h3>
          <div className="space-y-2">
            <Link to="/payroll"><Button variant="outline" className="w-full justify-start" data-testid="qa-payroll"><Wallet size={14} className="mr-2" /> Generate Payroll</Button></Link>
            <Link to="/invoices"><Button variant="outline" className="w-full justify-start" data-testid="qa-invoice"><FileText size={14} className="mr-2" /> Create Invoice</Button></Link>
            <Link to="/appointments"><Button variant="outline" className="w-full justify-start" data-testid="qa-appt"><CalendarBlank size={14} className="mr-2" /> Schedule Appointment</Button></Link>
            <Link to="/leads"><Button variant="outline" className="w-full justify-start" data-testid="qa-lead"><Phone size={14} className="mr-2" /> Add Lead</Button></Link>
            {isAdmin && (
              <Link to="/checkin-dashboard"><Button variant="outline" className="w-full justify-start" data-testid="qa-mark-absent"><CheckCircle size={14} className="mr-2" /> Mark Absent (EOD)</Button></Link>
            )}
          </div>
        </Card>
      </div>

      {/* Upcoming Appointments + Pending Reminders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="p-4" data-testid="dashboard-appointments-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-1.5"><CalendarBlank size={16} /> Upcoming Appointments (next 7 days)</h3>
            <Link to="/appointments" className="text-[11px] text-emerald-600 hover:underline">View all →</Link>
          </div>
          {(() => {
            const todayStr = today();
            const in7 = new Date(); in7.setDate(in7.getDate() + 7);
            const endStr = in7.toISOString().split('T')[0];
            const upcoming = (data.appointments || [])
              .filter((a) => a.date >= todayStr && a.date <= endStr && a.status !== 'Cancelled' && a.status !== 'Done')
              .sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time))
              .slice(0, 8);
            if (upcoming.length === 0) {
              return <div className="text-xs text-gray-400 text-center py-6">No upcoming appointments.</div>;
            }
            return (
              <div className="space-y-1.5">
                {upcoming.map((a) => {
                  const [y, m, d] = (a.date || '').split('-');
                  const dateLabel = y ? `${d}/${m}` : a.date;
                  const isToday = a.date === todayStr;
                  return (
                    <Link key={a.id} to="/appointments" className="block p-2 rounded border hover:bg-gray-50 transition" data-testid={`dash-appt-${a.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{a.title}</div>
                          <div className="text-[11px] text-gray-500 truncate">
                            {a.client_name ? `${a.client_name} · ` : ''}
                            {a.team_member_name ? `${a.team_member_name}` : ''}
                          </div>
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <div className={`text-[11px] font-semibold ${isToday ? 'text-amber-700' : 'text-gray-700'}`}>
                            {isToday ? 'TODAY' : dateLabel}
                          </div>
                          <div className="text-[11px] text-gray-500">{a.start_time}</div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            );
          })()}
        </Card>

        <Card className="p-4" data-testid="dashboard-reminders-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-1.5"><Bell size={16} /> Pending Reminders</h3>
            <Link to="/reminders" className="text-[11px] text-emerald-600 hover:underline">View all →</Link>
          </div>
          {(() => {
            const open = (data.reminders || []).filter((r) => r.status === 'open');
            if (open.length === 0) {
              return <div className="text-xs text-gray-400 text-center py-6">No pending reminders. 🎉</div>;
            }
            const priorityRank = { high: 0, medium: 1, low: 2 };
            const sorted = open.sort((a, b) => (priorityRank[a.priority] ?? 9) - (priorityRank[b.priority] ?? 9));
            return (
              <div className="space-y-1.5">
                {sorted.slice(0, 8).map((r) => {
                  const pColor = r.priority === 'high' ? 'bg-rose-50 text-rose-700' : r.priority === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-600';
                  return (
                    <Link key={r.id} to="/reminders" className="block p-2 rounded border hover:bg-gray-50 transition" data-testid={`dash-reminder-${r.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{r.title}</div>
                          <div className="text-[11px] text-gray-500 truncate">
                            From {r.created_by_name || '—'}
                            {r.department_name ? ` · ${r.department_name}` : ''}
                          </div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm font-medium uppercase ${pColor}`}>
                          {r.priority}
                        </span>
                      </div>
                    </Link>
                  );
                })}
                {open.length > 8 && (
                  <div className="text-[11px] text-gray-500 text-center pt-1">+ {open.length - 8} more</div>
                )}
              </div>
            );
          })()}
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
